import { useState, useEffect, Fragment } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Download,
  RefreshCw,
  FileText,
  Clock,
  Zap,
  Coins,
  CheckCircle,
  XCircle,
  Filter,
  Calendar,
  Smartphone,
  Server,
  ChevronLeft,
  MessageSquare,
  User,
  Bot,
  Settings2,
} from 'lucide-react';
import { logs, RequestLog, RequestLogDetail, keys, ApiKey } from '../api/client';
import { formatDateTime, formatNumber, formatCurrency, cn } from '../lib/utils';

type LogMessagePart = {
  type?: unknown;
  text?: unknown;
  image_url?: unknown;
  imageUrl?: unknown;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function getPartImageUrl(part: LogMessagePart) {
  if (isRecord(part.image_url) && typeof part.image_url.url === 'string') {
    return part.image_url.url;
  }
  if (typeof part.imageUrl === 'string') {
    return part.imageUrl;
  }
  return null;
}

function MessageContent({ content }: { content: unknown }) {
  if (typeof content === 'string') {
    return <div className="whitespace-pre-wrap text-foreground/90">{content}</div>;
  }

  if (Array.isArray(content)) {
    return (
      <div className="space-y-2">
        {content.map((rawPart, index) => {
          if (!isRecord(rawPart)) return null;

          const part = rawPart as LogMessagePart;
          if (part.type === 'text' && typeof part.text === 'string') {
            return (
              <div key={index} className="whitespace-pre-wrap text-foreground/90">
                {part.text}
              </div>
            );
          }

          const imageUrl = getPartImageUrl(part);
          if (part.type === 'image_url' && imageUrl) {
            return (
              <a
                key={index}
                href={imageUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="block rounded-lg overflow-hidden border border-border/40 bg-background/40 hover:border-cyan-500/40 transition-colors"
                title="Open image in new tab"
              >
                <img
                  src={imageUrl}
                  alt="Attached"
                  className="max-w-full max-h-80 object-contain"
                  loading="lazy"
                />
              </a>
            );
          }

          return null;
        })}
      </div>
    );
  }

  return <div className="whitespace-pre-wrap text-foreground/90">{String(content ?? '')}</div>;
}

export default function Logs() {
  const [requests, setRequests] = useState<RequestLog[]>([]);
  const [total, setTotal] = useState(0);
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [expandedDetails, setExpandedDetails] = useState<RequestLogDetail | null>(null);

  // Filters
  const [appKeyId, setAppKeyId] = useState('');
  const [deviceId, setDeviceId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(0);
  const limit = 50;

  const fetchData = async () => {
    setLoading(true);
    try {
      const [requestsRes, keysRes] = await Promise.all([
        logs.requests({
          appKeyId: appKeyId || undefined,
          deviceId: deviceId || undefined,
          startDate: startDate || undefined,
          endDate: endDate || undefined,
          limit,
          offset: page * limit,
        }),
        keys.list(),
      ]);

      setRequests(requestsRes.requests);
      setTotal(requestsRes.total);
      setApiKeys(keysRes.keys);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [appKeyId, deviceId, startDate, endDate, page]);

  const handleExpand = async (id: string) => {
    if (expandedId === id) {
      setExpandedId(null);
      setExpandedDetails(null);
      return;
    }

    try {
      const response = await logs.request(id);
      setExpandedId(id);
      setExpandedDetails(response.request);
    } catch (error) {
      console.error('Failed to fetch request details:', error);
    }
  };

  const handleExport = async (format: 'json' | 'csv') => {
    const blob = await logs.export(format, {
      appKeyId: appKeyId || undefined,
      deviceId: deviceId || undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `requests-export.${format}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const getAppName = (keyId: string) => {
    return apiKeys.find((k) => k.id === keyId)?.appName || keyId.slice(0, 8);
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between animate-fade-in-up">
        <div>
          <h1 className="text-3xl font-bold text-gradient">Request Logs</h1>
          <p className="text-muted-foreground mt-1">Monitor and analyze API requests</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleExport('csv')}
            className="btn-secondary px-4 py-2.5 rounded-xl flex items-center gap-2 text-sm"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
          <button
            onClick={() => handleExport('json')}
            className="btn-secondary px-4 py-2.5 rounded-xl flex items-center gap-2 text-sm"
          >
            <Download className="w-4 h-4" />
            Export JSON
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="card-glow rounded-2xl p-5 animate-fade-in-up animate-delay-100">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-4 h-4 text-cyan-400" />
          <span className="text-sm font-medium">Filters</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs text-muted-foreground mb-2">Application</label>
            <div className="relative">
              <Server className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <select
                value={appKeyId}
                onChange={(e) => {
                  setAppKeyId(e.target.value);
                  setPage(0);
                }}
                className="input-neural w-full pl-10 pr-4 py-2.5 rounded-xl text-sm appearance-none cursor-pointer"
              >
                <option value="">All Apps</option>
                {apiKeys.map((key) => (
                  <option key={key.id} value={key.id}>
                    {key.appName}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-2">Device ID</label>
            <div className="relative">
              <Smartphone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                value={deviceId}
                onChange={(e) => {
                  setDeviceId(e.target.value);
                  setPage(0);
                }}
                placeholder="Filter by device"
                className="input-neural w-full pl-10 pr-4 py-2.5 rounded-xl text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-2">Start Date</label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setPage(0);
                }}
                className="input-neural w-full pl-10 pr-4 py-2.5 rounded-xl text-sm"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-2">End Date</label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="date"
                value={endDate}
                onChange={(e) => {
                  setEndDate(e.target.value);
                  setPage(0);
                }}
                className="input-neural w-full pl-10 pr-4 py-2.5 rounded-xl text-sm"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Results */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center gap-3 text-muted-foreground">
            <RefreshCw className="w-5 h-5 animate-spin" />
            <span>Loading requests...</span>
          </div>
        </div>
      ) : (
        <>
          <div className="card-glow rounded-2xl overflow-hidden animate-fade-in-up animate-delay-200">
            <table className="table-neural min-w-full">
              <thead>
                <tr>
                  <th className="w-10 px-3"></th>
                  <th className="px-4 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Time
                  </th>
                  <th className="px-4 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    App
                  </th>
                  <th className="px-4 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Model
                  </th>
                  <th className="px-4 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Tokens
                  </th>
                  <th className="px-4 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Cost
                  </th>
                  <th className="px-4 py-4 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody>
                {requests.map((req, index) => (
                  <Fragment key={req.id}>
                    <tr
                      className="cursor-pointer transition-colors hover:bg-secondary/50"
                      onClick={() => handleExpand(req.id)}
                      style={{ animationDelay: `${(index + 3) * 30}ms` }}
                    >
                      <td className="px-3 py-4">
                        <div className="w-6 h-6 rounded-md bg-secondary flex items-center justify-center">
                          {expandedId === req.id ? (
                            <ChevronDown className="w-4 h-4 text-cyan-400" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Clock className="w-4 h-4" />
                          {formatDateTime(req.createdAt)}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span className="text-sm font-medium">{getAppName(req.appKeyId)}</span>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <code className="text-xs text-muted-foreground font-mono bg-secondary px-2 py-1 rounded">
                          {req.model}
                        </code>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Zap className="w-4 h-4 text-amber-400" />
                          <span className="text-sm font-mono">
                            {formatNumber(req.inputTokens + req.outputTokens)}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Coins className="w-4 h-4 text-emerald-400" />
                          <span className="text-sm font-mono">
                            {formatCurrency(req.estimatedCost)}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <span
                          className={cn(
                            'px-2.5 py-1 text-xs font-medium rounded-lg inline-flex items-center gap-1.5',
                            req.statusCode >= 200 && req.statusCode < 300
                              ? 'badge-active'
                              : 'bg-rose-500/15 text-rose-400 border border-rose-500/30'
                          )}
                        >
                          {req.statusCode >= 200 && req.statusCode < 300 ? (
                            <CheckCircle className="w-3 h-3" />
                          ) : (
                            <XCircle className="w-3 h-3" />
                          )}
                          {req.statusCode}
                        </span>
                      </td>
                    </tr>
                    {expandedId === req.id && expandedDetails && (
                      <tr>
                        <td colSpan={7} className="px-4 py-6 bg-secondary/30">
                          <div className="space-y-5">
                            {/* Details Grid */}
                            <div className="grid grid-cols-4 gap-4">
                              <div className="card-glow rounded-xl p-4">
                                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                                  <Smartphone className="w-3.5 h-3.5" />
                                  Device ID
                                </div>
                                <code className="text-sm font-mono">
                                  {expandedDetails.deviceId || 'N/A'}
                                </code>
                              </div>
                              <div className="card-glow rounded-xl p-4">
                                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                                  <Clock className="w-3.5 h-3.5" />
                                  Response Time
                                </div>
                                <span className="text-sm font-mono">
                                  {expandedDetails.responseTimeMs}ms
                                </span>
                              </div>
                              <div className="card-glow rounded-xl p-4">
                                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                                  <Zap className="w-3.5 h-3.5" />
                                  Input / Output
                                </div>
                                <span className="text-sm font-mono">
                                  {formatNumber(expandedDetails.inputTokens)} / {formatNumber(expandedDetails.outputTokens)}
                                </span>
                              </div>
                              <div className="card-glow rounded-xl p-4">
                                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1">
                                  <Settings2 className="w-3.5 h-3.5" />
                                  Streaming
                                </div>
                                <span className={cn(
                                  'text-sm',
                                  expandedDetails.isStreaming ? 'text-emerald-400' : 'text-muted-foreground'
                                )}>
                                  {expandedDetails.isStreaming ? 'Yes' : 'No'}
                                </span>
                              </div>
                            </div>

                            {/* Messages */}
                            <div>
                              <div className="flex items-center gap-2 mb-3">
                                <MessageSquare className="w-4 h-4 text-violet-400" />
                                <span className="text-sm font-medium">Conversation</span>
                              </div>
                              <div className="space-y-3 max-h-80 overflow-y-auto pr-2">
                                {expandedDetails.messages.map((msg, index) => (
                                  <div
                                    key={index}
                                    className={cn(
                                      'rounded-xl p-4 text-sm',
                                      msg.role === 'user'
                                        ? 'bg-cyan-500/10 border border-cyan-500/20 ml-12'
                                        : msg.role === 'assistant'
                                        ? 'bg-secondary border border-border mr-12'
                                        : 'bg-amber-500/10 border border-amber-500/20'
                                    )}
                                  >
                                    <div className="flex items-center gap-2 mb-2">
                                      {msg.role === 'user' ? (
                                        <User className="w-4 h-4 text-cyan-400" />
                                      ) : msg.role === 'assistant' ? (
                                        <Bot className="w-4 h-4 text-violet-400" />
                                      ) : (
                                        <Settings2 className="w-4 h-4 text-amber-400" />
                                      )}
                                      <span className="text-xs font-medium text-muted-foreground uppercase">
                                        {msg.role}
                                      </span>
                                    </div>
                                    <MessageContent content={msg.content as unknown} />
                                  </div>
                                ))}
                                {expandedDetails.responseContent && (
                                  <div className="bg-secondary border border-border rounded-xl p-4 text-sm mr-12">
                                    <div className="flex items-center gap-2 mb-2">
                                      <Bot className="w-4 h-4 text-violet-400" />
                                      <span className="text-xs font-medium text-muted-foreground uppercase">
                                        assistant (response)
                                      </span>
                                    </div>
                                    <div className="whitespace-pre-wrap text-foreground/90">
                                      {expandedDetails.responseContent}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
                {requests.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-6 py-16 text-center">
                      <div className="flex flex-col items-center gap-4">
                        <div className="w-16 h-16 rounded-2xl bg-secondary flex items-center justify-center">
                          <FileText className="w-8 h-8 text-muted-foreground" />
                        </div>
                        <div>
                          <p className="text-muted-foreground mb-1">No requests found</p>
                          <p className="text-sm text-muted-foreground/70">Try adjusting your filters</p>
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between animate-fade-in-up animate-delay-300">
              <p className="text-sm text-muted-foreground">
                Showing <span className="font-mono text-foreground">{page * limit + 1}</span> to{' '}
                <span className="font-mono text-foreground">{Math.min((page + 1) * limit, total)}</span> of{' '}
                <span className="font-mono text-foreground">{formatNumber(total)}</span> results
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="btn-secondary px-4 py-2 rounded-xl flex items-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </button>
                <div className="px-4 py-2 bg-secondary rounded-xl text-sm">
                  <span className="text-muted-foreground">Page</span>{' '}
                  <span className="font-mono">{page + 1}</span>{' '}
                  <span className="text-muted-foreground">of</span>{' '}
                  <span className="font-mono">{totalPages}</span>
                </div>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="btn-secondary px-4 py-2 rounded-xl flex items-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
